import { useMemo, useState } from "react"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { ButtonTertiary } from "@nova/component-btn"
import { Notification } from "@nova/schemas"
import { groupByDay } from "@nova/format"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { Drawer } from "@nova/component-drawer"
import { useRouter } from "pages"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { useNotifications, useMarkAllNotificationsRead } from "./NotificationQueries"
import { NotificationCard } from "./NotificationCard"
import { NotificationGroupedByDay } from "./Notification"
import { notificationMatchesTab } from "./notificationRegistry"
import { NotificationSettingsModal } from "./settings/NotificationSettingsModal"

type NotificationTab = "all" | "unread" | "mentions" | "jobs"

const tabs: { id: NotificationTab; label: string }[] = [
    { id: "all", label: "All" },
    { id: "unread", label: "Unread" },
    { id: "mentions", label: "Mentions" },
    { id: "jobs", label: "Jobs" },
]

const filterByTab = (notifications: Notification[], tab: NotificationTab) => {
    switch (tab) {
        case "all":
            return notifications
        case "unread":
            return notifications.filter((n) => !n.readAt)
        case "mentions":
            return notifications.filter((n) => notificationMatchesTab(n, "mentions"))
        case "jobs":
            return notifications.filter((n) => notificationMatchesTab(n, "jobs"))
    }
}

export const NotificationsPanel = () => {
    const [activeTab, setActiveTab] = useState<NotificationTab>("all")
    const [showSettings, setShowSettings] = useState(false)
    const { notifications, isLoading, unreadCount } = useNotifications()
    const { markAllNotificationsRead } = useMarkAllNotificationsRead()
    const router = useRouter("/")
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) || ""

    const sections = useMemo(() => {
        if (!notifications) return []
        return groupByDay(filterByTab(notifications, activeTab), (n) => n.createdAt)
    }, [notifications, activeTab])

    return (
        <div className="h-full flex flex-col text-white">
            <div className="flex items-center justify-between px-5 pt-5 pb-4">
                <h2 className="text-lg text-white">Notifications</h2>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        aria-label="Notification settings"
                        className="text-white-400 hover:text-white transition-colors"
                        onClick={() => setShowSettings(true)}
                    >
                        <Icons.SmallIcon Icon={Icons.NotificationSettings} />
                    </button>
                    {workspaceId && (
                        <Drawer.Close>
                            <ButtonTertiary
                                title="Activity"
                                LeftIcon={Icons.PlannerReview}
                                onClick={() =>
                                    router.navigate({
                                        path: "/workspace/:workspaceId/explore/activity",
                                        params: { workspaceId },
                                    })
                                }
                            />
                        </Drawer.Close>
                    )}
                    <Drawer.Close>
                        <button
                            type="button"
                            aria-label="Close notifications"
                            className="text-white-400 hover:text-white transition-colors"
                        >
                            <Icons.SmallIcon Icon={Icons.XClose} />
                        </button>
                    </Drawer.Close>
                </div>
            </div>

            <div className="flex items-center justify-between px-5">
                <div className="flex items-center gap-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={classNames(
                                "px-3 py-1.5 rounded-lg transition-colors",
                                activeTab === tab.id
                                    ? "bg-gray-700 text-white"
                                    : "text-gray-300 hover:text-white"
                            )}
                        >
                            <small>
                                {tab.label}
                                {tab.id === "unread" && ` (${unreadCount})`}
                            </small>
                        </button>
                    ))}
                </div>
                {unreadCount > 0 && (
                    <button
                        type="button"
                        onClick={() => markAllNotificationsRead()}
                        className="text-blue-500 hover:text-blue-400 transition-colors"
                    >
                        <small>Mark all as read</small>
                    </button>
                )}
            </div>

            <ScrollableArea className="grow px-5">
                {isLoading && (
                    <div className="flex items-center justify-center h-full">
                        <div className="loader-sm" />
                    </div>
                )}
                {!isLoading && sections.length === 0 && (
                    <div className="flex flex-col items-center justify-center gap-6 py-10">
                        <div className="flex items-center justify-center w-[103px] h-[103px] rounded-full border-2 border-green-700">
                            <Icons.ExtraLargeIcon Icon={Icons.AlarmOn} />
                        </div>
                        <div className="flex flex-col items-center gap-1 text-center">
                            <p className="text-base text-white whitespace-nowrap">
                                You’re all caught up.
                            </p>
                            <small className="text-gray-300">No unread notifications.</small>
                        </div>
                    </div>
                )}
                {!isLoading && sections.length > 0 && (
                    <NotificationGroupedByDay>
                        <div className="flex flex-col gap-6 pb-4">
                            {sections.map((section) => (
                                <div key={section.label} className="flex flex-col gap-2">
                                    <small className="text-gray-300 px-2 tracking-wide py-6">
                                        {section.label.toUpperCase()}
                                    </small>
                                    <div className="flex flex-col gap-2">
                                        {section.items.map((notification) => (
                                            <NotificationCard
                                                key={notification.id}
                                                notification={notification}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </NotificationGroupedByDay>
                )}
            </ScrollableArea>

            {showSettings && <NotificationSettingsModal onClose={() => setShowSettings(false)} />}
        </div>
    )
}
